/**
 * RequireAuth Guard
 *
 * Route guard that requires user to be authenticated.
 * Redirects to login page if not authenticated.
 *
 * @example
 * <RequireAuth>
 *   <DashboardPage />
 * </RequireAuth>
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectIsInitialized } from '../store/authSelectors';
import { Box, CircularProgress } from '@mui/material';

interface RequireAuthProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitialized = useSelector(selectIsInitialized);

  // Note: `redirectTo` is kept for API compatibility, but not used.
  // Keycloak handles redirects via `login-required` in initKeycloak().
  void redirectTo;

  // Show loading while initializing auth
  if (!isInitialized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated after initialization, Keycloak's login-required
  // will have already triggered a redirect, so just show loading
  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
};
